import { z } from "zod";
import { PhysicalSceneSchema } from "./physical-scene.js";
import { NarrativeDeclarationSchema, NarrativeDevelopmentSchema } from "./narrative-arc.js";
import { ProseSchema } from "./prose.js";
import { DocumentKindSchema } from "./primitives.js";
import { NarratorVoiceSchema, NarrativeTextureSchema, ExpressionMemorySchema, ExpressionUseSchema } from "./narrator-expression.js";
/** Shared output ceiling; purpose targets may be shorter and are never minimums. */
export const NARRATION_WORD_CEILING = 400;
/** Presentation-only facts admitted by code. Future beats and hidden evidence never belong here. */
export const NarrativeDocumentReceiptSchema = z.strictObject({
    documentId: z.string().min(1),
    title: z.string().min(1),
    /** A speakable label; normally the title, or the document kind if the title uses banned vocabulary. */
    label: ProseSchema,
    /** The bible's authored aliases for this artifact, speakable ones only: what a person would call it. Any of them names the arrival. */
    aliases: z.array(ProseSchema).max(8).optional(),
    kind: DocumentKindSchema,
    source: z.string().min(1),
    createdAt: z.string().min(1),
    /** Opaque runtime fingerprint binding the delivered copy; never a narratable edition label. */
    edition: z.string().min(1),
    change: z.enum(["arrived", "upgraded"]),
    channel: z.enum(["email", "artifact", "field_impression"]),
    limitation: ProseSchema.nullable(),
    coverLine: ProseSchema.nullable(),
});
export const NarrativeTurnContextSchema = z.strictObject({
    version: z.literal(1),
    physicalScene: PhysicalSceneSchema.optional(),
    purpose: z.enum(["clarification", "acknowledgement", "answer", "exchange", "conversation", "document", "offer", "consequence", "transition"]),
    direction: z.strictObject({ purpose: z.string(), guidance: z.string(), process: z.array(NarrativeDeclarationSchema), developments: z.array(NarrativeDevelopmentSchema).optional() }).optional(),
    // Accept earlier context snapshots; new turns always use NARRATION_WORD_CEILING.
    pacing: z.strictObject({ targetMin: z.number().int().nonnegative(), targetMax: z.number().int().positive(), ceiling: z.union([z.literal(200), z.literal(NARRATION_WORD_CEILING)]) }).optional(),
    place: z.string().min(1).nullable(),
    voices: z.array(z.strictObject({ name: z.string(), profile: NarratorVoiceSchema })).max(6).optional(),
    texture: NarrativeTextureSchema.optional(),
    expressionMemory: ExpressionMemorySchema.optional(),
    /** Mentioning someone is not proof they are physically present. */
    participants: z.array(z.strictObject({ name: z.string().min(1), channel: z.enum(["present", "remote", "unknown"]) })),
    documents: z.array(NarrativeDocumentReceiptSchema),
    /** Previously delivered artifacts explicitly referenced by the learner; opening never means comprehension. */
    references: z.array(z.strictObject({
        documentId: z.string().min(1),
        title: z.string().min(1),
        opened: z.boolean(),
        referenceLine: ProseSchema.nullable(),
    })),
    /** In-fiction clauses, not instructions such as 'show who hands this over'. */
    requiredBeats: z.array(z.strictObject({ kind: z.string().min(1), text: ProseSchema, performance: z.string().min(1).optional() })),
});
/** Final delivery lineage, recorded once after all remedies. No transcript text in these counters. */
export const NarrationDeliverySchema = z.strictObject({
    version: z.literal(1),
    origin: z.enum(["generated", "authored_fallback", "emergency_fallback", "authored_opening"]),
    generationAttempts: z.number().int().min(0),
    reasons: z.array(z.string().min(1)),
    promptVersion: z.string().min(1),
    elapsedMs: z.number().nonnegative(),
    purpose: NarrativeTurnContextSchema.shape.purpose.optional(),
    words: z.number().int().nonnegative().optional(),
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
    obligationsOverCeiling: z.boolean().optional(),
    expression: ExpressionUseSchema.optional(),
});
//# sourceMappingURL=narrative.js.map